/**
 * Submission Cache (Issue #3227)
 *
 * Caches form submission data in localStorage to prevent data loss.
 * If a server action fails, the data persists and can be retried.
 *
 * Key format: cadence-submission:{type}:{contextId}
 * Example: cadence-submission:update:rock:abc123
 *
 * `'use client'` is REQUIRED here, and load-bearing beyond documentation. This
 * module is localStorage-only and every importer is browser code, so the
 * directive is true either way — but `check:no-direct-crypto-randomuuid` reaches
 * files outside its `SCAN_ROOTS` (`src/components`, `src/hooks`) only by
 * detecting this directive. Without it, `generateSubmissionId` below is
 * unscanned, and that is the one call site whose own comment argues the id is a
 * capability: `reflections.ts` looks a submission up by id alone, so a guessable
 * id short-circuits the action. The guard's SCOPE section names this file; the
 * directive is what makes that claim true (#12811 review).
 */
'use client'

import { safeRandomUUID } from '@/lib/browser-uuid'

export const SUBMISSION_PREFIX = 'cadence-submission:'
export const SUBMISSION_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours
export const SUBMISSION_MAX_SIZE_BYTES = 5 * 1024 * 1024 // 5MB

/** SessionStorage key for the build timestamp captured at page load */
export const BUILD_TIMESTAMP_KEY = 'cadence-initial-build-timestamp'

type CachedEntry = {
  data: unknown
  timestamp: number
  submissionId: string
}

export type PendingSubmission = {
  key: string
  data: unknown
  timestamp: number
  submissionId: string
}

function fullKey(key: string): string {
  return `${SUBMISSION_PREFIX}${key}`
}

function isExpired(timestamp: number): boolean {
  return Date.now() - timestamp > SUBMISSION_EXPIRY_MS
}

/**
 * Estimate total size of all submission cache entries in bytes.
 */
function getSubmissionCacheSize(): number {
  let totalSize = 0
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(SUBMISSION_PREFIX)) {
        const value = localStorage.getItem(key)
        if (value) {
          // Approximate byte size (UTF-16 chars * 2 bytes)
          totalSize += (key.length + value.length) * 2
        }
      }
    }
  } catch {
    // localStorage unavailable
  }
  return totalSize
}

/**
 * Generate a unique submission ID for idempotent retries.
 *
 * `safeRandomUUID()` rather than a bare `crypto.randomUUID()`: the native API
 * is undefined outside a secure context (a dev server reached over Tailscale or
 * the LAN), and this module is browser-only — it drives `localStorage` — so it
 * hits that path (#12799).
 *
 * The previous version caught that throw and returned
 * `${Date.now()}-${Math.random()...}`: ~46 bits of non-cryptographic entropy
 * plus a millisecond timestamp, silently, forever. That is not acceptable for
 * THIS id, because a submissionId is a capability, not just a dedupe key —
 * `src/app/actions/reflections.ts` looks a submission up by id alone
 * (org-scoped but not user-scoped) on the documented assumption that
 * "submissionIds are crypto UUIDs so they cannot be guessed", and nothing
 * validates the shape. So a guessable id short-circuits that action to success.
 * `safeRandomUUID` falls back to `crypto.getRandomValues` (which is NOT gated
 * on a secure context) and throws only when the page has no WebCrypto at all;
 * `cacheSubmission`'s own `catch` turns that into a `null` submissionId, which
 * every caller already handles as "not cached".
 */
function generateSubmissionId(): string {
  return safeRandomUUID()
}

/**
 * Cache submission data in localStorage before sending to server.
 * Generates a submissionId for idempotent retries.
 * Returns the submissionId on success, null if storage fails.
 */
export function cacheSubmission(key: string, data: unknown): string | null {
  try {
    // Reuse existing submissionId if entry already cached
    const existing = localStorage.getItem(fullKey(key))
    let submissionId: string
    if (existing) {
      try {
        const parsed: CachedEntry = JSON.parse(existing)
        submissionId = parsed.submissionId ?? generateSubmissionId()
      } catch {
        submissionId = generateSubmissionId()
      }
    } else {
      submissionId = generateSubmissionId()
    }

    const entry: CachedEntry = {
      data,
      timestamp: Date.now(),
      submissionId,
    }
    const serialized = JSON.stringify(entry)

    // Check if adding this entry would exceed the budget
    // Subtract existing entry size to avoid double-counting on updates
    const entrySize = (fullKey(key).length + serialized.length) * 2
    const currentSize = getSubmissionCacheSize()
    const existingSize = existing
      ? (fullKey(key).length + existing.length) * 2
      : 0
    if (currentSize - existingSize + entrySize > SUBMISSION_MAX_SIZE_BYTES) {
      return null
    }

    localStorage.setItem(fullKey(key), serialized)
    return submissionId
  } catch {
    return null
  }
}

/**
 * Retrieve cached submission data if it exists and hasn't expired.
 * Expired entries are automatically cleaned up.
 */
export function getCachedSubmission<T = unknown>(key: string): T | null {
  try {
    const raw = localStorage.getItem(fullKey(key))
    if (!raw) return null

    const entry: CachedEntry = JSON.parse(raw)

    if (isExpired(entry.timestamp)) {
      localStorage.removeItem(fullKey(key))
      return null
    }

    return entry.data as T
  } catch {
    return null
  }
}

/**
 * Remove a cached submission (call after successful submit).
 */
export function clearSubmission(key: string): void {
  try {
    localStorage.removeItem(fullKey(key))
  } catch {
    // Silently fail
  }
}

/**
 * Get all non-expired pending submissions for retry.
 */
export function getPendingSubmissions(): PendingSubmission[] {
  const pending: PendingSubmission[] = []

  try {
    // Collect keys first to avoid mutation during iteration
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(SUBMISSION_PREFIX)) {
        keys.push(key)
      }
    }

    for (const storageKey of keys) {
      const raw = localStorage.getItem(storageKey)
      if (!raw) continue

      try {
        const entry: CachedEntry = JSON.parse(raw)

        if (isExpired(entry.timestamp)) {
          localStorage.removeItem(storageKey)
          continue
        }

        pending.push({
          key: storageKey.slice(SUBMISSION_PREFIX.length),
          data: entry.data,
          timestamp: entry.timestamp,
          submissionId: entry.submissionId,
        })
      } catch {
        // Skip corrupted entries
      }
    }
  } catch {
    // localStorage unavailable
  }

  return pending
}

/**
 * Check if there are any pending (non-expired) submissions in cache.
 */
export function hasPendingSubmissions(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(SUBMISSION_PREFIX)) {
        const raw = localStorage.getItem(key)
        if (raw) {
          const entry: CachedEntry = JSON.parse(raw)
          if (!isExpired(entry.timestamp)) return true
        }
      }
    }
  } catch {
    // localStorage unavailable
  }
  return false
}

/**
 * Remove all cached submissions (preserves other localStorage data).
 */
export function clearAllSubmissions(): void {
  try {
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(SUBMISSION_PREFIX)) {
        keysToRemove.push(key)
      }
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key)
    }
  } catch {
    // Silently fail
  }
}
